import React from 'react';
import { Button, Input, Modal, Form, Space, Empty, message } from 'antd';
import { FormOutlined, CloseOutlined } from '@ant-design/icons';
import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import addApplicationIcon from 'assets/images/add-application.svg';
import { getApplications, addApplication, getApplication, updateApplication } from 'api';
import { usePermissions } from '../../hooks';
import * as Permissions from '../../constants/permissions';

import './index.css';

const ApplicationManagement = () => {
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [form] = Form.useForm();
	const [isEditMode, setIsEditMode] = useState(false);

	const [applications, setApplications] = useState([]);
	const [appAttributes, setAppAttributes] = useState([
		{ id: Date.now(), key: '', value: '', placeholder: 'Please input value' },
	]);

	const { permissions } = useSelector((state) => state.user);

	const [loading, setLoading] = useState(false);
	const [submitting, setSubmitting] = useState(false);

	const selectedApp = useRef(null);

	useEffect(() => {
		fetchApplications();
	}, []);

	const fetchApplications = async () => {
		try {
			setLoading(true);
			getApplications().then((data) => setApplications(data));
		} catch (error) {
			console.error('Failed to fetch applications:', error);
		} finally {
			setLoading(false);
		}
	};

	// MARK: Add/Edit app actions
	const appAttributeChanged = (id, field, value) => {
		const newParams = appAttributes.map((param) => (param.id === id ? { ...param, [field]: value } : param));
		setAppAttributes(newParams);
		form.setFieldsValue({ params: newParams });

		if (field === 'key' && value) {
			const keys = newParams.filter((param) => param.key).map((param) => param.key);
			const duplicateKeys = keys.filter((key, idx) => keys.indexOf(key) !== idx);
			if (duplicateKeys.includes(value)) {
				message.warning(`Key "${value}" already exists`);
			}
		}
	};

	const addAppAttribute = () => {
		const newParam = {
			id: Date.now(),
			key: '',
			value: '',
			placeholder: 'Please input value',
		};
		const newParams = [...appAttributes, newParam];
		setAppAttributes(newParams);
		form.setFieldsValue({ params: newParams });
	};

	const removeAppAttribute = (id) => {
		const newParams = appAttributes.filter((param) => param.id !== id);
		setAppAttributes(newParams);
		form.setFieldsValue({ params: newParams });
	};

	const handleSubmit = () => {
		if (!form) {
			message.error('Form initialization failed');
			return;
		}

		form
			.validateFields()
			.then(async (values) => {
				try {
					setSubmitting(true);
					const keys = appAttributes.filter((param) => param.key).map((param) => param.key);
					const duplicateKeys = keys.filter((key, index) => keys.indexOf(key) !== index);
					if (duplicateKeys.length > 0) {
						message.error(`Duplicate key found: ${duplicateKeys.join(', ')}`);
						return;
					}

					const validParams = appAttributes.filter((param) => param.key && param.value);

					const payload = {
						applicationName: values.applicationName || '',
						description: values.appDescription || '',
						applicationAttributeList: validParams.map((param) => ({
							attributeName: param.key,
							attributeValue: param.value,
						})),
					};
					if (selectedApp.current) {
						await updateApplication(selectedApp.current.application?.id, payload);
					} else {
						await addApplication(payload);
					}
					await fetchApplications();
					noCloseModal();
					message.success(isEditMode ? 'Application updated successfully' : 'Application added successfully');
				} catch (error) {
					console.error('Operation failed:', error);
				} finally {
					setSubmitting(false);
				}
			})
			.catch((errorInfo) => {
				console.log('Form validation failed:', errorInfo);
			});
	};

	// MARK: - Application add/edit
	const onAddApplication = () => {
		setIsEditMode(false);
		selectedApp.current = null;
		setIsModalVisible(true);
	};

	const onEditApplication = async (application) => {
		setIsEditMode(true);
		setIsModalVisible(true);
		selectedApp.current = application;
		try {
			const appData = await getApplication(application.id);
			if (appData) {
				selectedApp.current = appData;
				form.setFieldsValue({
					applicationName: appData?.application?.applicationName,
					appDescription: appData.application?.description,
				});
				if (appData.applicationAttributeList && appData.applicationAttributeList.length > 0) {
					const paramsData = appData.applicationAttributeList.map((attr, idx) => ({
						id: Date.now() + idx,
						key: attr.attributeName,
						value: attr.attributeValue,
						placeholder: 'Please input value',
					}));
					setAppAttributes(paramsData);
					form.setFieldsValue({ params: paramsData });
				} else {
					const emptyParams = [{ key: '', value: '', placeholder: 'Please input value' }];
					setAppAttributes(emptyParams);
					form.setFieldsValue({ params: emptyParams });
				}
			}
		} catch (error) {
			console.error('Failed to fetch application details:', error);
		} finally {
		}
	};

	const noCloseModal = () => {
		selectedApp.current = null;
		form.resetFields();
		const initialParams = [{ id: Date.now(), key: '', value: '', placeholder: 'Please input value' }];
		setAppAttributes(initialParams);
		form.setFieldsValue({ params: initialParams });
		setIsModalVisible(false);
	};

	return (
		<div className="application-management-container">
			<div className="header">
				<h1>Application</h1>
			</div>
			<div>
				<div className="app-list">
					<div className="applications-list">
						<div
							ref={usePermissions([Permissions.Application_ADD], permissions)}
							className="add-app"
							onClick={onAddApplication}
						>
							<img alt="" src={addApplicationIcon} />
						</div>
						{/* )} */}
					</div>
					{loading ? (
						<div className="loading-container"></div>
					) : applications.length === 0 ? (
						<div className="empty-container">
							<Empty description="No applications found" image={Empty.PRESENTED_IMAGE_SIMPLE} />
						</div>
					) : (
						applications.map((app) => (
							<div key={app.id} className="application-card">
								<div className="application-info">
									<div className="application-details">
										<div className="card-title-container">
											<div className="application-name">{app.applicationName}</div>
											{permissions.includes(Permissions.Application_EDIT) && (
												<div className="editIcon" onClick={() => onEditApplication(app)}>
													<FormOutlined />
												</div>
											)}
										</div>
										<div className="application-description">{app.description}</div>
									</div>
								</div>
							</div>
						))
					)}
				</div>
			</div>
			<Modal
				className="application-management-modal"
				title={isEditMode ? 'Edit Application' : 'Add Application'}
				open={isModalVisible}
				onCancel={noCloseModal}
				width={960}
				footer={null}
				closeIcon={<img src={require('assets/images/closeBtn.png')} className="app-modal-close-btn" />}
			>
				<Form layout="vertical" name="add_application_form" form={form}>
					<div className="modal-section">
						<div className="section-title">Basic Info</div>
						<div className="form-row-app">
							<Form.Item
								className="form-item-app-name"
								name="applicationName"
								required
								label="Application Name"
								rules={[
									{
										required: true,
										message: 'Application Name cannot be null',
									},
								]}
							>
								<Input className="param-key-name" placeholder="" disabled={isEditMode} />
							</Form.Item>
							<div className="form-item-app-des">
								<Form.Item
									name="appDescription"
									label="Application Description"
									required
									rules={[
										{
											required: true,
											message: 'Application Description cannot be null',
										},
									]}
								>
									<Input className="param-value-des" placeholder="" />
								</Form.Item>
							</div>
						</div>
					</div>
					<div className="modal-section">
						<div className="section-title">
							Environment Configuration <span style={{ color: '#ff4d4f' }}>*</span>
						</div>
					</div>
					<div className="modal-section environmentConfig-section">
						<Form.Item
							name="environmentConfig"
							validateTrigger="onSubmit"
							rules={[
								{
									validator: (_, value) => {
										const keys = appAttributes.filter((param) => param.key).map((param) => param.key);
										const duplicateKeys = keys.filter((key, index) => keys.indexOf(key) !== index);
										if (duplicateKeys.length > 0) {
											return Promise.reject(new Error(`Duplicate key found: ${duplicateKeys.join(', ')}`));
										}
										return Promise.resolve();
									},
								},
							]}
						>
							<div className="parameters-container">
								<div>
									{appAttributes.map((param, index) => (
										<div className="param-con-text" key={param.id}>
											<Space.Compact style={{ width: '100%' }}>
												<Form.Item
													name={['params', index, 'key']}
													label="key"
													rules={[
														{
															required: true,
															message: 'Key cannot be empty',
														},
													]}
												>
													<Input
														className="param-key"
														placeholder="please input key"
														onChange={(e) => appAttributeChanged(param.id, 'key', e.target.value)}
													/>
												</Form.Item>
												<Form.Item
													name={['params', index, 'value']}
													label="value"
													rules={[
														{
															required: true,
															message: 'Value cannot be empty',
														},
													]}
												>
													<Input
														className="param-value"
														placeholder="please input value"
														onChange={(e) => appAttributeChanged(param.id, 'value', e.target.value)}
													/>
												</Form.Item>
											</Space.Compact>
											<div className="remove-param" onClick={() => removeAppAttribute(param.id)}>
												<CloseOutlined className="close-icon" style={{ color: '#3E4248', fontSize: '14px' }} />
											</div>
										</div>
									))}
								</div>
							</div>
						</Form.Item>
						<div className="add-paramater" onClick={addAppAttribute}>
							+ Add Parameter
						</div>
					</div>
					<div className="modal-footer">
						<Button className="cancelbtn" key="cancel" onClick={noCloseModal} disabled={submitting}>
							Cancel
						</Button>
						<Button className="addbtn" color="purple" variant="solid" onClick={handleSubmit} loading={submitting}>
							{isEditMode ? 'Save' : 'Add'}
						</Button>
					</div>
				</Form>
			</Modal>
		</div>
	);
};

export default ApplicationManagement;
